import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import { CaretRight } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { difference, union } from 'es-toolkit';
import * as React from 'react';
import styled from 'styled-components';

import Folder from './components/Folder';
import { Folder as FolderType } from './types/Folder';
import { InboxFolderStats } from './types/InboxFolderStats';

type Props = {
    readonly activeFolder: number | null;
    readonly className?: string;
    readonly formatNumber: Function;
    readonly hideUserFolders?: boolean;
    readonly inboxFolders: InboxFolderStats;
    readonly loading: boolean;
    readonly onArchiveFolder: Function;
    readonly onCreateFolder: Function;
    readonly onRenameFolder: Function;
    readonly onSetActiveInbox: Function;
    readonly testid?: string;
};

const Folders = ({
    activeFolder = 0,
    className,
    formatNumber = (num: number) => num,
    hideUserFolders,
    inboxFolders,
    loading = false,
    onArchiveFolder,
    onCreateFolder,
    onRenameFolder = (name: any) => name,
    onSetActiveInbox,
    testid = 'messengerFolders',
}: Props) => {
    const { systemFolders = [], userFolders = [] } = inboxFolders;
    const [edit, setEdit] = React.useState(false);
    const [expand, setExpand] = React.useState(false);
    const [name, setName] = React.useState('');
    const [archiveing, setArchiving] = React.useState<any>([]);
    const [renaming, setRenaming] = React.useState<any>([]);

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            onCreateFolder(name.trim());
            setName('');
        }
        event.stopPropagation();
    };

    const handleButtonClick = () => {
        if (name.trim()) {
            onCreateFolder(name.trim());
            setName('');
        }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        setName(target.value);
        event.stopPropagation();
    };

    const handleCancel = () => {
        setEdit(false);
        setExpand(false);
        setArchiving([]);
        setRenaming([]);
    };

    const inboxFolder: FolderType = systemFolders.find((f) => f.id === 0) as FolderType;
    const sentFolder: FolderType = systemFolders.find((f) => f.id === 1) as FolderType;
    const archiveFolder: FolderType = systemFolders.find((f) => f.id === -1) as FolderType;

    return (
        <div className={className}>
            <FolderList data-testid={testid}>
                {Boolean(inboxFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={inboxFolder.unReadConversationCount && formatNumber(inboxFolder.unReadConversationCount)}
                        id={inboxFolder.id}
                        key={inboxFolder.id}
                        loading={loading}
                        name="Inbox"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
                {Boolean(sentFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={sentFolder.unReadConversationCount && formatNumber(sentFolder.unReadConversationCount)}
                        id={sentFolder.id}
                        key={sentFolder.id}
                        loading={loading}
                        name="Sent"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
                {Boolean(archiveFolder?.name) && (
                    <Folder
                        activeFolder={activeFolder}
                        count={
                            archiveFolder.unReadConversationCount && formatNumber(archiveFolder.unReadConversationCount)
                        }
                        id={archiveFolder.id}
                        key={archiveFolder.id}
                        loading={loading}
                        name="Archive"
                        onSetActiveInbox={onSetActiveInbox}
                    />
                )}
            </FolderList>
            {!hideUserFolders && (
                <StyledContainer>
                    <StyledHeader>
                        <StyledFolderLabel color="secondary">Folders</StyledFolderLabel>
                        {!edit ? (
                            <StyledActionText onClick={() => setEdit(!edit)}>
                                <BodySecondary>Edit</BodySecondary>
                            </StyledActionText>
                        ) : (
                            <div>
                                <StyledActionText onClick={() => setExpand(!expand)}>
                                    <BodySecondary>New</BodySecondary>
                                </StyledActionText>
                                <StyledPipe> | </StyledPipe>
                                <StyledActionText onClick={handleCancel}>
                                    <BodySecondary>Cancel</BodySecondary>
                                </StyledActionText>
                            </div>
                        )}
                    </StyledHeader>
                    <StyledInputContainer $expand={expand}>
                        <Input
                            aria-label="New folder name"
                            hideSpacer
                            id="newFolder"
                            label="New folder name"
                            onChange={handleOnChange}
                            onKeyPress={handleKeyPress}
                            placeholder="New folder name"
                            small
                            value={name}
                        />
                        <StyledIconContainer onClick={handleButtonClick}>
                            <CaretRight size="xs" />
                        </StyledIconContainer>
                    </StyledInputContainer>
                    <FancyFolderList>
                        {userFolders.map(({ id, name, unReadConversationCount }) => (
                            <Folder
                                activeFolder={activeFolder}
                                archive={archiveing.includes(id)}
                                count={unReadConversationCount && formatNumber(unReadConversationCount)}
                                edit={edit}
                                id={id}
                                key={id}
                                loading={loading}
                                name={name}
                                onArchiveFolder={onArchiveFolder}
                                onRenameFolder={onRenameFolder}
                                onSetActiveInbox={onSetActiveInbox}
                                renaming={renaming.includes(id)}
                                setArchive={(archive: boolean) =>
                                    archive
                                        ? setArchiving(union(archiveing, [id]))
                                        : setArchiving(difference(archiveing, [id]))
                                }
                                setRenaming={(rename: boolean) =>
                                    rename
                                        ? setRenaming(union(renaming, [id]))
                                        : setRenaming(difference(renaming, [id]))
                                }
                            />
                        ))}
                    </FancyFolderList>
                </StyledContainer>
            )}
        </div>
    );
};

export default Folders;

const FolderList = styled.ul`
    padding: 0;
    margin-bottom: 10px;
`;

const StyledContainer = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    padding-top: 15px;
`;

const StyledHeader = styled.div`
    margin-bottom: 6px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
`;

const StyledFolderLabel = styled(BodySecondary)`
    text-transform: uppercase;
`;

const FancyFolderList = styled.ul`
    padding: 0;
    margin-bottom: 20px;
    max-height: 1200px;
    height: auto;
    overflow: auto;
`;

const StyledPipe = styled.span`
    color: ${({ theme }) => theme.colors.grey300};
`;

const StyledIconContainer = styled.div`
    background: ${({ theme }) => theme.colors.blue100};
    width: 30px;
    height: 34px;
    margin-top: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.blue200};
    }

    svg {
        color: ${({ theme }) => theme.colors.white};
    }
`;

const StyledActionText = styled(ActionText)`
    span {
        color: ${({ theme }) => theme.colors.blue100};
        &:hover {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const StyledInputContainer = styled.div<{ $expand?: boolean }>`
    overflow: hidden;
    width: 204px;
    display: flex;
    align-items: center;
    flex-direction: row;
    padding: 0 10px;
    transition: height 0.25s;
    height: ${({ $expand }) => ($expand ? '56px' : '0')};
    max-height: ${({ $expand }) => ($expand ? '56px' : '0')};
`;
